import { useMemo } from "react";
import snakeCase from "lodash.snakecase";
import * as R from "ramda";
import { useQueries, useQuery } from "react-query";

import { useCurrentTenant } from "contexts/currentTenant";
import { useVersionedClientRef } from "contexts/versionedClient";
import { getUseCodeConditionAnswers } from "reducers/projects";
import { getUseCodes } from "reducers/tenant";
import zoningApi from "services/zoningApi";
import { ZoningClearances } from "types/entities";
import useIsTest from "utils/useIsTest";
import { getAllRules, selectZoningData } from "utils/zoning";

import { getAnswer } from "./projects";
import { useTile } from "./tiles";

export const useClearances = (useCodeSlug: string, options = {}) =>
  useQuery<ZoningClearances>(
    ["clearances", useCodeSlug],
    () => zoningApi.getClearances(useCodeSlug),
    {
      enabled: !!useCodeSlug,
      staleTime: Infinity,
      cacheTime: Infinity,
      ...options, // Allows passing additional options like 'select'
    },
  );

const selectClearancesWithRules = ({ clearances, radiusRules }) => ({
  clearances,
  radiusRules,
  allRules: getAllRules(clearances),
});

export const useClearancesWithRules = (useCodeSlug: string) =>
  useClearances(useCodeSlug, {
    select: selectClearancesWithRules,
  });

export const useZoningData = ({ record, lat, lng, z = 16 }) => {
  const answers = useMemo(() => getUseCodeConditionAnswers(record), [record]);
  const useCodeSlug = R.path(["use", "slug"], getAnswer(record, "project_type") || {}) as string;

  const isTest = useIsTest();

  const {
    data: clearancesData = { clearances: [], radiusRules: [] },
    isLoading: clearancesLoading,
    error: clearancesError,
  } = useClearances(useCodeSlug, {
    enabled: !!useCodeSlug && !isTest,
  });
  const { clearances } = clearancesData;

  const zonesTileQuery = useTile("zones", { lat, lng, z }, { enabled: !isTest });
  const parcelsTileQuery = useTile("parcels", { lat, lng, z }, { enabled: !isTest });
  const rulesTileQuery = useTile(
    "rules",
    { lat, lng, z },
    {
      enabled: !!useCodeSlug && !isTest,
    },
  );

  const isLoading = [
    zonesTileQuery.isLoading,
    parcelsTileQuery.isLoading,
    ...(useCodeSlug ? [rulesTileQuery.isLoading, clearancesLoading] : []),
  ].some(Boolean);

  const error = [
    zonesTileQuery.error,
    parcelsTileQuery.error,
    ...(useCodeSlug ? [rulesTileQuery.error, clearancesError] : []),
  ].find(Boolean);

  const { tile: zonesTile, x, y } = zonesTileQuery.data || {};
  const { tile: parcelsTile } = parcelsTileQuery.data || {};
  const { tile: rulesTile } = rulesTileQuery.data || {};
  const zoningAnswer = getAnswer(record, "zoning");

  const data = useMemo(() => {
    if (isTest) {
      return zoningAnswer;
    }

    if (isLoading || !lat || !lng) {
      return null;
    }

    if (error) {
      return null;
    }
    return selectZoningData({
      lat,
      lng,
      x,
      y,
      z,
      useCodeSlug,
      answers,
      clearances,
      zonesTile,
      parcelsTile,
      rulesTile,
    });
  }, [
    isLoading,
    isTest,
    zoningAnswer,
    lat,
    lng,
    z,
    useCodeSlug,
    error,
    zonesTile,
    parcelsTile,
    rulesTile,
    answers,
    clearances,
    x,
    y,
  ]);

  if (error) {
    return { isError: true, error };
  }

  return {
    isLoading,
    data,
  };
};

interface SluggedRecord {
  slug: string;
}
const slugEq = (s: string) => (r: SluggedRecord) => R.propEq(s, "slug", r);

export const useUseCodesForQuery = (query, queryOpts = {}) => {
  const tenant = useCurrentTenant();
  const client = useVersionedClientRef();

  return useQuery({
    queryKey: ["zoning", "use_codes", query],
    queryFn() {
      const slugMatch = /^use_code:([a-zA-Z0-9_-]+)$/.exec(query);
      if (slugMatch) {
        const slug: string = snakeCase(slugMatch[1]).replaceAll("_", "-");
        return R.filter(slugEq(slug), getUseCodes(tenant) as SluggedRecord[]);
      }
      return getUseCodesForQuery({ query, client });
    },
    ...queryOpts,
  });
};

export const useUseCodesKeywordsQuery = (keywords: string[], opts = {}) => {
  const client = useVersionedClientRef();

  return useQueries(
    keywords.map((keyword) => ({
      queryKey: ["zoning", "use_codes", "keywords", keyword], // key differs from useUseCodesForQuery due to different return format
      async queryFn() {
        const useCodes = await getUseCodesForQuery({ query: keyword, client });
        return { text: keyword, results: useCodes };
      },
      ...opts,
    })),
  );
};

async function getUseCodesForQuery({ query, client }) {
  const { data } = await client.current.get("/api/zoning/uses", {
    params: {
      "filter[query_string]": query,
    },
  });
  return R.compose(R.map(R.prop("attributes")), R.prop("data"))(data);
}
